
import React, { FC, useEffect, useRef, useState } from "react";
import { Flex, Input, TableProps, Tooltip, Tag, notification, DatePicker, Select, Space } from "antd";
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";
import STableLayout, {
    DEFAULT_PAGE_SIZE,
    TableLayoutRef,
} from "@/styles/STableLayout";
import { useTranslation } from "react-i18next";
import useDataList from "@/hooks/useDataList";
import { CommonListParams, SourceTypeEnum } from "@/types/common";
import apis from "@/apis/supplier";
import { getStorage } from "@/utils/storage";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import SText from "@/styles/SText";

import { ConfirmedEnum, OperateEnum } from "@/types/inventory";

import { newList } from "@/apis/dict";
import dayjs, { Dayjs } from "dayjs";
import { OrderItemV2 } from "@/types/inventoryV2";
import InventoryDetailV2 from "./components/InventoryDetailV2";
import PrintQC, { PrintType } from "./components/PrintQC";
import PrintInventoryV2, { PrintType as InventoryType } from "./components/PrintInventoryV2";
import { numFormmater } from "@/pages/Requirement/util";
import { getSourceNumber } from "@/utils/getInventorySourceNo";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName } from "@/utils/user";

const { RangePicker } = DatePicker;


interface InventoryOrderProps {
    supplierId?: string;
}

interface InventoryOrderFilters {
    keyword: string;
    startTime: Dayjs | null;
    endTime: Dayjs | null;
    confirmed: string | undefined;
}

const INITIAL_INVENTORY_ORDER_FILTERS: InventoryOrderFilters = {
    keyword: "",
    startTime: null,
    endTime: null,
    confirmed: "0",
};

const InventoryOrder: FC<InventoryOrderProps> = ({ supplierId }) => {
    const { t } = useTranslation();

    const tableRef = useRef<TableLayoutRef | null>(null);
    const [list, total, listLoading, { fetchList }] = useDataList<OrderItemV2, [string, string, CommonListParams<string>?]>({ fetchApi: supplierId ? apis.inventoryOrdersConfirmV2 : apis.inventoryOrdersV2 });
    const {
        draftFilters,
        appliedFilters,
        updateDraftFilters,
        commitFilters,
        replaceFilters,
    } = useCommittedFilters<InventoryOrderFilters>(INITIAL_INVENTORY_ORDER_FILTERS);
    const { keyword, startTime, endTime, confirmed } = draftFilters;
    const [order, setOrder] = useState<OrderItemV2 | null>(null);
    const [printAddress, setPrintAddress] = useState<string>('');
    const [printLoading, setPrintLoading] = useState<boolean>(false);
    const [selectedRows, setSelectedRows] = useState<OrderItemV2[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<React.Key>>([]);

    const [exportLoading, setExportLoading] = useState<boolean>(false);
    const [exportProgress, setExportProgress] = useState<number>(0);


    useEffect(() => {
        newList('PRINT_SETTING').then(res => {
            res.map(item => {
                if (item.key === "companyLocation") {
                    setPrintAddress(item.value)
                }
            })
        }).catch(err => {
            console.log('err', err)
        }).finally(() => {
            setPrintLoading(false)
        })
    }, []);

    useEffect(() => {
        const nextFilters = replaceFilters(INITIAL_INVENTORY_ORDER_FILTERS);
        fetch(0, nextFilters);
        tableRef.current?.resetTablePage();
        // 供应商入口切换时重置筛选，查询由事件显式提交。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supplierId, replaceFilters])

    // 清理选中的行，确保不包含禁用项
    useEffect(() => {
        if (list.length > 0 && selectedRowKeys.length > 0) {
            const validKeys = selectedRowKeys.filter(key => {
                const item = list.find(row => row.id === key);
                if (!item) return false;
                const isDisabled = item.order?.confirmState === ConfirmedEnum.CONFIRM && item.order?.opType === OperateEnum.IN_RETURN;
                return !isDisabled;
            });

            const validRows = validKeys.map(key => list.find(row => row.id === key)).filter(Boolean) as OrderItemV2[];

            // 如果有无效的选中项，清理状态
            if (validKeys.length !== selectedRowKeys.length) {
                setSelectedRowKeys(validKeys);
                setSelectedRows(validRows);
            }
        }
    }, [list]);


    const columns: TableProps<OrderItemV2>["columns"] = [
        {
            title: <SText text={t("warehouse.refNo")} />,
            dataIndex: ["source", "number"],
            width: 180,
            fixed: 'left',
            sorter: (a, b) => {
                const valA = getSourceNumber(a);
                const valB = getSourceNumber(b);
                return valA.localeCompare(valB);
            },
            render: (_, item) => <SText text={getSourceNumber(item)} style={{ width: "100%" }} />,
        },
        {
            title: <SText text={t("purchase.orderNumber")} />,
            dataIndex: ["order", "number"],
            width: 180,
            fixed: "left",
            sorter: (a, b) => {
                const valA = a?.order?.number ?? "";
                const valB = b?.order?.number ?? "";
                return valA.localeCompare(valB);
            },
            render: (_, item) => (
                <SText text={item?.order?.number} style={{ width: "100%" }} />
            ),
        },
        {
            title: <SText text={t("order.quantity")} />,
            key: "item",
            dataIndex: "count",
            width: 80,
            sorter: (a, b) => (a?.count ?? 0) - (b?.count ?? 0),
            render: (_, item) => (
                <SText text={numFormmater(item?.count || 0)} style={{ width: "100%" }} />
            ),
        },
        {
            title: t("purchase.productInfo"),
            children: [
                {
                    title: <SText text={t("product.code")} />,
                    dataIndex: ["product", "code"],
                    width: 100,
                    sorter: (a, b) => {
                        const valA = a?.product?.code ?? "";
                        const valB = b?.product?.code ?? "";
                        return valA.localeCompare(valB);
                    },
                    render: (_, item) => (
                        <SText text={item?.product?.code ?? ''} style={{ width: "100%" }} />
                    ),
                },
                {
                    title: <SText text={t("product.name")} />,
                    dataIndex: "name",
                    width: 100,
                    render: (_, item) => (
                        <SText text={item?.product?.name ?? ''} style={{ width: "100%" }} />
                    ),
                },
                {
                    title: <SText text={t("product.brand1")} />,
                    dataIndex: "model1",
                    width: 120,
                    render: (_, item) => (
                        <SText text={item?.product?.model ?? ''} style={{ width: "100%" }} />
                    ),
                },
                {
                    title: <SText text={t("product.uom")} />,
                    dataIndex: "uom",
                    width: 120,
                    render: (_, item) => (
                        <SText text={item?.product?.uom ?? ''} style={{ width: "100%" }} />
                    ),
                },
            ],
        },
        {
            title: <SText text={t("common.creator")} />,
            dataIndex: "creator",
            width: 100,
            render: (_, item) => {
                let userData = item.creator;
                return (
                    userData && <Flex>
                        <div>
                            <WMSAvatar creator={userData} uniChar={userData.id}
                                avatarId={userData.avatar}
                                size={24} alt={getUserAvatarTip(userData)}
                            />
                        </div>
                        <SText text={getUserName(userData)} />
                    </Flex>
                )
            },
        },
        {
            title: <SText text={t("purchase.stocked")} />,
            width: 120,
            render(value, item, index) {
                return (
                    item?.order?.confirmState === ConfirmedEnum.CONFIRM ? (
                        item?.order?.opType === OperateEnum.IN_RETURN ? <Tag color="red" >{t('purchase.inReturn')}</Tag> : item?.order?.opType === OperateEnum.OUT_RETURN ? <Tag color="green" >{t('purchase.cancelReturn')}</Tag> : <Tag color={'blue'}>{t('purchase.inStock')}</Tag>
                    )
                        : <Tag color={"warning"}>{t('purchase.notInStock')}</Tag>
                );
            },
        },
        {
            title: <SText text={t('order.preTime')} />,
            width: 120,
            render: (_, item) => item?.order?.arrivalAt && <SText text={dayjs(item.order.arrivalAt).format('YYYY-MM-DD')} />
        },
        {
            title: <SText text={t('purchase.receivingUnit')} />,
            width: 120,
            render: (_, item) => <SText text={item.sourceType === SourceTypeEnum.PURCHASE_ITEM ? item?.source?.order?.receiver ?? '' : ''} />
        },
        {
            title: <SText text={t('purchase.receivingAddress')} />,
            width: 120,
            render: (_, item) => <SText text={item.sourceType === SourceTypeEnum.PURCHASE_ITEM ? item?.source?.order?.receiverAddress ?? '' : ''} />
        },
        {
            title: <SText text={t('sale.contact')} />,
            width: 120,
            render: (_, item) => <SText text={item.sourceType === SourceTypeEnum.PURCHASE_ITEM ? item?.source?.order?.receiverContact ?? '' : ''} />
        },
        {
            title: <SText text={t('purchase.phone1')} />,
            width: 120,
            render: (_, item) => <SText text={item.sourceType === SourceTypeEnum.PURCHASE_ITEM ? item?.source?.order?.receiverMobile ?? '' : ''} />
        },
        // {
        //     title: <SText text={t("purchase.operate")} />,
        //     dataIndex: "operate",
        //     width: 80,
        //     fixed: "right",
        //     render: (_, record) => <Flex justify="space-evenly" style={{ width: "100%" }}>
        //         <Button onClick={() => setOrder(record)}>{t('common.detail')}</Button>
        //     </Flex>
        // }
    ];


    const fetch = (offset: number, filters: InventoryOrderFilters) => {
        const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
        fetchList(supplierId ?? getStorage("SUPPLIER_ID"), supplierId ? (filters.confirmed ?? '') : '', {
            body: filters.keyword,
            pagination: { offset, take },
            startTime: filters.startTime ? filters.startTime.startOf("day").valueOf() : undefined,
            endTime: filters.endTime ? filters.endTime.endOf("day").valueOf() : undefined,
        }).catch((err) => {
            switch (err) {
                case 10010:
                    break;
                default:
                    break;
            }
        });
    };

    // 导出列配置
    const exportColumns = (t: any) => [
        { key: "warehouse.refNo", label: t("warehouse.refNo") },
        { key: "purchase.orderNumber", label: t("purchase.orderNumber") },
        { key: "order.quantity", label: t("order.quantity") },
        { key: "product.code", label: t("product.code") },
        { key: "product.name", label: t("product.name") },
        { key: "product.brand1", label: t("product.brand1") },
        { key: "product.uom", label: t("product.uom") },
        { key: "purchase.stocked", label: t("purchase.stocked") },
        { key: "order.preTime", label: t("order.preTime") },
        { key: "purchase.receivingUnit", label: t("purchase.receivingUnit") },
        { key: "purchase.receivingAddress", label: t("purchase.receivingAddress") },
        { key: "sale.contact", label: t("sale.contact") },
        { key: "purchase.phone1", label: t("purchase.phone1") },
    ];

    // 导出数据映射函数
    const exportDataMapper = (item: OrderItemV2, t: any) => {
        const getStockStatus = () => {
            if (item?.order?.confirmState === ConfirmedEnum.CONFIRM) {
                if (item?.order?.opType === OperateEnum.IN_RETURN) return t('purchase.inReturn');
                if (item?.order?.opType === OperateEnum.OUT_RETURN) return t('purchase.cancelReturn');
                return t('purchase.inStock');
            }
            return t('purchase.notInStock');
        };
        return {
            [t('warehouse.refNo')]: getSourceNumber(item),
            [t('purchase.orderNumber')]: item?.order?.number ?? '',
            [t('order.quantity')]: item?.count ?? '',
            [t('product.code')]: item?.product?.code ?? '',
            [t('product.name')]: item?.product?.name ?? '',
            [t('product.brand1')]: item?.product?.model ?? '',
            [t('product.uom')]: item?.product?.uom ?? '',
            [t('purchase.stocked')]: getStockStatus(),
            [t('order.preTime')]: item?.order?.arrivalAt ? dayjs(item.order.arrivalAt).format('YYYY-MM-DD') : '',
            [t('purchase.receivingUnit')]: item.sourceType === SourceTypeEnum.PURCHASE_ITEM ? item?.source?.order?.receiver ?? '' : '',
            [t('purchase.receivingAddress')]: item.sourceType === SourceTypeEnum.PURCHASE_ITEM ? item?.source?.order?.receiverAddress ?? '' : '',
            [t('sale.contact')]: item.sourceType === SourceTypeEnum.PURCHASE_ITEM ? item?.source?.order?.receiverContact ?? '' : '',
            [t('purchase.phone1')]: item.sourceType === SourceTypeEnum.PURCHASE_ITEM ? item?.source?.order?.receiverMobile ?? '' : '',
        };
    };

    // 导出全部数据
    const handleExportAll = async (): Promise<OrderItemV2[]> => {
        setExportLoading(true);
        setExportProgress(0);
        try {
            const id = supplierId ?? getStorage("SUPPLIER_ID");
            const res = supplierId
                ? await apis.inventoryOrdersConfirmV2(id, appliedFilters.confirmed ?? '', {
                    body: appliedFilters.keyword,
                    startTime: appliedFilters.startTime ? appliedFilters.startTime.startOf("day").valueOf() : undefined,
                    endTime: appliedFilters.endTime ? appliedFilters.endTime.endOf("day").valueOf() : undefined,
                })
                : await apis.inventoryOrdersV2(id, '', {
                    body: appliedFilters.keyword,
                    startTime: appliedFilters.startTime ? appliedFilters.startTime.startOf("day").valueOf() : undefined,
                    endTime: appliedFilters.endTime ? appliedFilters.endTime.endOf("day").valueOf() : undefined,
                });
            setExportProgress(100);
            return res?.body ?? [];
        } catch (error) {
            throw error;
        } finally {
            setExportLoading(false);
            setExportProgress(0);
        }
    };

    const handleSearch = () => {
        const nextFilters = commitFilters();
        tableRef.current?.resetTablePage();
        fetch(0, nextFilters);
    };

    const handleClearFilters = () => {
        const nextFilters = replaceFilters(INITIAL_INVENTORY_ORDER_FILTERS);
        tableRef.current?.resetTablePage();
        fetch(0, nextFilters);
    };

    const headerChildren = (
        <Flex justify="space-between" align="center" gap={12} style={{ width: "100%" }}>
            <Space>
                <PrintInventoryV2 disabled={listLoading || printLoading} showBottom showHeader
                    dictAddress={printAddress} onStart={() => setPrintLoading(true)}
                    onEnd={() => setPrintLoading(false)} selectedRows={selectedRows} printType={InventoryType.NORMAL} />
                <PrintQC disabled={listLoading || printLoading}
                    dictAddress={printAddress} printType={PrintType.SUP}
                    onStart={() => { setPrintLoading(true) }} onEnd={() => { setPrintLoading(false) }}
                    selectedRows={selectedRows} />
                <ExportExcelButton<OrderItemV2>
                    list={list}
                    columns={exportColumns}
                    dataMapper={exportDataMapper}
                    fileName="delivery_orders.xlsx"
                    sheetName={t('purchase.delivery')}
                />
                <ExportExcelButton<OrderItemV2>
                    isAll
                    loadData={handleExportAll}
                    columns={exportColumns}
                    dataMapper={exportDataMapper}
                    fileName="delivery_orders_all.xlsx"
                    sheetName={t('purchase.delivery')}
                />
            </Space>
            <div style={{ flex: 1, minWidth: 0 }}>
                <ListFilterBar
                    onSearch={handleSearch}
                    onReset={handleClearFilters}
                    disabled={listLoading || printLoading}
                >
                    {({ submit }) => (
                        <>
                            {supplierId && <Select value={confirmed} style={{ width: 200 }}
                                placeholder={t('purchase.stocked')} allowClear
                                options={[
                                    { label: t('purchase.inStock'), value: '1' },
                                    { label: t('purchase.notInStock'), value: '0' },
                                ]}
                                onChange={value => updateDraftFilters({ confirmed: value })} />}
                            <RangePicker
                                value={[startTime, endTime]}
                                onChange={value => updateDraftFilters({
                                    startTime: value?.[0] ?? null,
                                    endTime: value?.[1] ?? null,
                                })} />
                            <Tooltip title={t("supplierLayout.searchByKeyword")}>
                                <Input
                                    placeholder={t("supplierLayout.searchByKeyword")}
                                    style={{ width: 200 }}
                                    value={keyword}
                                    onChange={event => updateDraftFilters({ keyword: event.target.value.trim() })}
                                    onPressEnter={submit}
                                />
                            </Tooltip>
                        </>
                    )}
                </ListFilterBar>
            </div>
        </Flex>
    );

    const timeComputed = (t1: number | null, t2: number | null) => {
        if (t1 === t2) return true
        else if (t1 && t2) {
            return dayjs(t1).endOf('day').valueOf() === dayjs(t2).endOf('day').valueOf()
        }
        else return false;
    }


    return (
        <Flex vertical style={{ width: "100%", height: "100%" }}>
            <STableLayout
                loading={listLoading || printLoading}
                dataSource={list}
                columns={columns as ColumnsType<AnyObject>}
                scrollX={1000}
                headerChildren={headerChildren}
                tableLayoutRef={tableRef}
                total={total}
                handleChange={offset => fetch(offset, appliedFilters)}
                rowKey={'id'}
                rowSelection={{
                    selectedRowKeys,
                    onChange(selectedRowKeys_opt, selectedRows_opt, { type }) {
                        // 获取实际可选择的行（过滤掉禁用的项）
                        const getSelectableRows = (rows: OrderItemV2[]) => {
                            return rows.filter(row => {
                                const isDisabled = row.order?.confirmState === ConfirmedEnum.CONFIRM && row.order?.opType === OperateEnum.IN_RETURN;
                                return !isDisabled;
                            });
                        };


                        if (type === "all") {
                            // 全选逻辑
                            if (selectedRowKeys?.length > 0) {
                                setSelectedRows([])
                                setSelectedRowKeys([])
                            } else {
                                // 找出第一行作为基准时间
                                let baseRow = list.find(item => {
                                    const isDisabled = item.order?.confirmState === ConfirmedEnum.CONFIRM && item.order?.opType === OperateEnum.IN_RETURN;
                                    return !isDisabled; // 找第一个未被禁用的行
                                });

                                if (!baseRow) {
                                    // 如果没有可选择的行，直接返回
                                    setSelectedRows([])
                                    setSelectedRowKeys([])
                                    return;
                                }

                                const allSelects = list.filter(item => {
                                    // 首先检查是否被禁用
                                    const isDisabled = item.order?.confirmState === ConfirmedEnum.CONFIRM && item.order?.opType === OperateEnum.IN_RETURN;
                                    if (isDisabled) return false;
                                    // 然后检查时间条件
                                    return timeComputed(item?.order?.arrivalAt || null, baseRow?.order?.arrivalAt || null);
                                });

                                setSelectedRows([...allSelects])
                                setSelectedRowKeys(allSelects.map(i => i.id))
                            }
                            return
                        }

                        // 手动选择逻辑
                        const selectableSelectedRows = getSelectableRows(selectedRows_opt as OrderItemV2[]);

                        if (selectableSelectedRows.length === 0) {
                            // 如果没有可选择项，清空选择
                            setSelectedRows([])
                            setSelectedRowKeys([])
                            return;
                        }

                        if (selectableSelectedRows.length === 1) {
                            // 只选择一项，直接设置
                            setSelectedRows(selectableSelectedRows)
                            setSelectedRowKeys(selectableSelectedRows.map(i => i.id))
                            return;
                        }

                        // 检查所有选择项的时间条件是否一致
                        const baseTime = selectableSelectedRows[0]?.order?.arrivalAt || null;
                        const allSameTime = selectableSelectedRows.every(item =>
                            timeComputed(item?.order?.arrivalAt || null, baseTime)
                        );

                        if (!allSameTime) {
                            // 如果时间不一致，回退到之前的选择状态
                            return notification.error({
                                placement: 'top',
                                message: t('purchase.notSelect'),
                                description: t('purchase.selectDateError')
                            })
                        }

                        // 所有检查通过，更新选择
                        setSelectedRows(selectableSelectedRows)
                        setSelectedRowKeys(selectableSelectedRows.map(i => i.id))
                    },
                    getCheckboxProps(record) {
                        return {
                            disabled: record.order?.confirmState === ConfirmedEnum.CONFIRM && record.order?.opType === OperateEnum.IN_RETURN, // 已入库且非退货单不可选
                        }
                    },
                }}
            />
            <InventoryDetailV2 order={order} onCancel={() => setOrder(null)} />
            <ExportProgressModal visible={exportLoading} progress={exportProgress} />
        </Flex>
    );
};

export default InventoryOrder;
